import { useEffect, useRef } from "react";
import { GrundrissState } from "../../arrangement/grundrissFunctions";
import { CoordinateSystem } from "./CoordinateSystem";
import { MAufbauten } from "../../../metamodell";
import { AufbauMenuTeil } from "./AufbauMenuTeil";
import { UndoRedo } from "../UndoRedo";
import { Zoom } from "./Zoom";
import { findAuswahlwerte } from "../Auswahl";
import { isSchiebetorForm } from "../../arrangement/elementFunctions";
import { TorMenuTeil } from "./TorMenuTeil";
import { StandardFormPopover } from "./StandardFormPopover";
import { resetGrundrissCamera } from "../../pages/Grundriss";
import { useExternalState } from "oc-ui/react/useExternalState";

interface LeftMenuProps {
  state: Partial<GrundrissState>;
  cs: CoordinateSystem;
  setSelected: (s: unknown) => void;
  setNewState: (newState: Partial<GrundrissState>) => void;
  setDollyDirty: () => void;
}

export function LeftMenu({
  state,
  cs,
  setSelected,
  setNewState,
  setDollyDirty,
}: LeftMenuProps): JSX.Element {
  const [grundformOpen, setGrundformOpen] = useExternalState(
    APP.arrangement.grundrissDialogOpen,
    (v) => (APP.arrangement.grundrissDialogOpen = v)
  );
  const showTrafoAufbau = APP.arrangement.trafoTrees.length > 0;
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);

  // ───────────────────────── click‑outside handler ──────────────────────────
  useEffect(() => {
    function handleClickOutside(ev: MouseEvent) {
      const target = ev.target as Node;
      const insideDialog = dialogRef.current?.contains(target);
      const insideButton = anchorRef.current?.contains(target);
      if (grundformOpen && !insideDialog && !insideButton) {
        setGrundformOpen(false);
      }
    }
    document.addEventListener("pointerdown", handleClickOutside, true);
    return () => document.removeEventListener("pointerdown", handleClickOutside, true);
  });

  return (
    <>
      <nav className="left-menu" role="navigation" aria-label={I18N("aria::Grundriss-Werkzeuge")} data-group-key="Grundriss-Werkzeuge">
        <div className="icons">
          <button
            ref={anchorRef}
            title={I18N("Grundform")}
            aria-label={I18N("Grundform")}
            aria-expanded={grundformOpen}
            aria-haspopup="true"
            className={"action-item bordered grundform-button"}
            data-button-key="Grundform"
            onClick={(e) => {
              const newValue = !grundformOpen;
              APP.logUserAction(
                { action: "standardFormPopoverOpen", value: newValue },
                { stateChange: false }
              );
              setGrundformOpen(newValue);
            }}
          >
            <div className="icon" aria-hidden="true">
              ▣
            </div>
            <span className="title">{I18N("Grundform")}</span>
            <div className="chevron-wrapper">
              <div className="divider" />
              <div className="chevron" aria-hidden="true">
                {" "}
                ❯{" "}
              </div>
            </div>
          </button>
        </div>

        <div
          className="icons tool-buttons"
          role="toolbar"
          aria-label={I18N("aria::Zeichenwerkzeuge")}
          data-group-key="Zeichenwerkzeuge"
        >
          {["zeichnen", "radieren", "verschieben", "selektieren"].map((key) => (
            <button
              key={key}
              title={I18N(key)}
              aria-label={I18N(key)}
              aria-pressed={key == state.modus}
              data-button-key={key}
              className={"action-item bordered " + (key == state.modus ? "selected" : "")}
              onMouseUp={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                setNewState({ modus: key as any });
                setSelected(null);
                APP.logUserAction({ action: "toolChange", modus: key }, { stateChange: false });
              }}
            >
              <div className={"icon " + key} aria-hidden="true"></div>
              <span className="title">{I18N(key)}</span>
            </button>
          ))}
        </div>

        <div
          className="aufbauten-menu bordered"
          role="group"
          aria-label={I18N("aria::Tore und Aufbauten")}
          data-group-key="Tore-und-Aufbauten"
        >
          {state.ready &&
            findAuswahlwerte({ Key: "Grundrisstore" }).auswahlwerte.map((auswahlwert) => {
              const isDoppeltor = auswahlwert.Key == "Grundriss_Doppeltor";
              const isSchiebetor = auswahlwert.Key == "Grundriss_Schiebetor";
              const detailSerieHasDoppeltore = APP.arrangement.inspiration?.DetailSerie.Tor?.some(
                (tor) => tor.Doppeltor
              );
              const detailSerieHasSchiebetore = APP.arrangement.inspiration?.DetailSerie.Tor?.some(
                (tor) => isSchiebetorForm(tor.Mass?.Form)
              );
              return (
                <TorMenuTeil
                  key={auswahlwert.id}
                  auswahlwert={auswahlwert}
                  setSelected={setSelected}
                  disabled={
                    (isDoppeltor && !detailSerieHasDoppeltore) ||
                    (isSchiebetor && !detailSerieHasSchiebetore)
                  }
                />
              );
            })}

          {APP.metadata.Aufbauten.filter((a) => a.Key !== "Trafo" || showTrafoAufbau).map(
            (aufbauMeta: MAufbauten) => (
              <AufbauMenuTeil
                key={aufbauMeta.Key}
                cs={cs}
                aufbauMeta={aufbauMeta}
                setSelected={setSelected}
              />
            )
          )}
        </div>

        <UndoRedo />

        <Zoom setDollyDirty={setDollyDirty} />
      </nav>
      {grundformOpen && anchorRef.current && (
        <StandardFormPopover
          ref={dialogRef}
          anchorRef={anchorRef}
          removeDialogFunction={async () => {
            setGrundformOpen(false);
            resetGrundrissCamera();
          }}
        />
      )}
    </>
  );
}
